import { atom, useRecoilState, useResetRecoilState } from "recoil";
import { useCallback } from "react";
import { randomString } from "../../Functions";
import { ProgressState, UseProgressReturn } from "./type";
import * as topikAPI from "../../Data/Topik";

// Progress state atom
export const progressState = atom<ProgressState>({
    key: "progressState" + randomString(4),
    default: {
        items: {},
        loading: false,
        error: null
    }
});

export const useProgress = (): UseProgressReturn => {
    const [state, setState] = useRecoilState(progressState);
    const resetProgress = useResetRecoilState(progressState);

    // Get progress for an attempt
    const getProgress = useCallback(async (attemptid: string): Promise<topikAPI.ProgressResponse | null> => {
        // Return cached progress if available and recent (within 30 seconds)
        const cached = state.items[attemptid];
        if (cached) {
            // For active attempts, always fetch fresh data
            if (cached.status === 'active') {
                // Continue to fetch fresh data below
            } else {
                // For completed attempts, use cached data
                return cached;
            }
        }

        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const progress = await topikAPI.getProgress(attemptid);
            
            if (progress) {
                setState(prev => ({
                    ...prev,
                    items: { ...prev.items, [attemptid]: progress },
                    loading: false
                }));
                
                return progress;
            }
            
            setState(prev => ({ ...prev, loading: false }));
            return null;
        } catch (error) {
            console.error('Failed to get progress:', error);
            setState(prev => ({ 
                ...prev, 
                loading: false, 
                error: 'Failed to load progress' 
            }));
            return null;
        }
    }, [state.items, setState]);

    // Force refresh progress
    const refreshProgress = useCallback(async (attemptid: string): Promise<void> => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const progress = await topikAPI.getProgress(attemptid);
            
            if (progress) {
                setState(prev => ({
                    ...prev,
                    items: { ...prev.items, [attemptid]: progress },
                    loading: false
                }));
            } else {
                setState(prev => ({ ...prev, loading: false }));
            }
        } catch (error) {
            console.error('Failed to refresh progress:', error);
            setState(prev => ({ 
                ...prev, 
                loading: false, 
                error: 'Failed to refresh progress' 
            }));
        }
    }, [setState]);

    const reset = useCallback(() => {
        resetProgress();
    }, [resetProgress]);

    return {
        progress: state.items,
        loading: state.loading,
        error: state.error,
        getProgress,
        refreshProgress,
        reset
    };
};